import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calculator, Info, ExternalLink, Loader2, CheckCircle, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
  {
    id: 'lawn-care',
    name: 'Lawn Care',
    minimumCharge: '$85 + GST',
    minimumNote: 'If lawn is under 305m² it will fall into our minimum charge of $85. Pricing can be altered depending on which machine we use - push mower or zero turn.',
    hasCalculator: true,
    calculatorType: 'area',
    rate: 0.28,
    minimumValue: 85,
    minimumArea: 305,
    maxArea: 1000,
    gstInclusive: false,
    inputLabel: 'Lawn Size (m²)',
    inputPlaceholder: 'Enter lawn size (max 1000m²)',
  },
  {
    id: 'lawn-care-rideon',
    name: 'Lawn Care (Ride On)',
    minimumCharge: 'Quote on request',
    minimumNote: 'For properties over 1000m². We will use Google Earth to measure your property and be in contact regarding your quote.',
    hasCalculator: false,
    gstInclusive: false,
  },
  {
    id: 'pressure-cleaning',
    name: 'Pressure Cleaning',
    minimumCharge: '$200',
    hasCalculator: true,
    calculatorType: 'area',
    rate: 3,
    minimumValue: 200,
    gstInclusive: false,
    inputLabel: 'Area Size (m²)',
    inputPlaceholder: 'Enter area size in square metres',
  },
  {
    id: 'solar-panel-clean',
    name: 'Solar Panel Clean',
    minimumCharge: '$200 inc. GST',
    hasCalculator: true,
    calculatorType: 'panels',
    rate: 18,
    minimumValue: 200,
    gstInclusive: true,
    inputLabel: 'Number of Panels',
    inputPlaceholder: 'Enter number of solar panels',
  },
  {
    id: 'roof-clean',
    name: 'Roof Clean',
    minimumCharge: '$200 inc. GST',
    hasCalculator: true,
    calculatorType: 'area',
    rate: 4,
    minimumValue: 200,
    gstInclusive: true,
    inputLabel: 'Roof Area (m²)',
    inputPlaceholder: 'Enter roof area in square metres',
  },
  {
    id: 'gutter-clean',
    name: 'Gutter Clean',
    minimumCharge: '$200 inc. GST',
    hasCalculator: true,
    calculatorType: 'length',
    rate: 5,
    minimumValue: 200,
    gstInclusive: true,
    inputLabel: 'Gutter Length (metres)',
    inputPlaceholder: 'Enter total gutter length in metres',
  },
  {
    id: 'exterior-window-clean',
    name: 'Exterior Window Clean',
    minimumCharge: '$200',
    hasCalculator: true,
    calculatorType: 'windows',
    paneRate: 8,
    screenRate: 4,
    minimumValue: 200,
    gstInclusive: false,
    inputLabel: 'Number of Window Panes',
    inputLabel2: 'Number of Fly Screens',
    inputPlaceholder: 'Enter number of window panes',
    inputPlaceholder2: 'Enter number of fly screens',
  },
];

const australianStates = [
  { value: 'QLD', label: 'Queensland' },
  { value: 'NSW', label: 'New South Wales' },
  { value: 'VIC', label: 'Victoria' },
  { value: 'SA', label: 'South Australia' },
  { value: 'WA', label: 'Western Australia' },
  { value: 'TAS', label: 'Tasmania' },
  { value: 'NT', label: 'Northern Territory' },
  { value: 'ACT', label: 'Australian Capital Territory' },
];

export default function QuoteModal({ isOpen, onClose, preselectedService }) {
  // Steps: 'form', 'calculator', 'complete'
  const [step, setStep] = useState('form');
  const [selectedService, setSelectedService] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const [inputValue2, setInputValue2] = useState('');
  const [calculatedQuote, setCalculatedQuote] = useState(null);
  const [showMaxAreaWarning, setShowMaxAreaWarning] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionId, setSubmissionId] = useState(null);
  
  const [formData, setFormData] = useState({
    fullName: '',
    address: '',
    suburb: '',
    state: 'NSW',
    postcode: '',
    email: '',
    phone: '',
    description: '',
  });

  useEffect(() => {
    if (preselectedService) {
      const service = services.find(s => s.id === preselectedService.id);
      setSelectedService(service);
    }
  }, [preselectedService]);

  // Calculator logic
  useEffect(() => {
    if (!selectedService || !selectedService.hasCalculator) {
      setCalculatedQuote(null);
      setShowMaxAreaWarning(false);
      return;
    }

    const value = parseFloat(inputValue) || 0;
    const value2 = parseFloat(inputValue2) || 0;

    if (selectedService.id === 'lawn-care' && selectedService.maxArea && value > selectedService.maxArea) {
      setShowMaxAreaWarning(true);
      setCalculatedQuote(null);
      return;
    } else {
      setShowMaxAreaWarning(false);
    }

    if (value <= 0 && selectedService.calculatorType !== 'windows') {
      setCalculatedQuote(null);
      return;
    }

    let quote = 0;

    if (selectedService.calculatorType === 'area' || selectedService.calculatorType === 'length') {
      quote = value * selectedService.rate;
    } else if (selectedService.calculatorType === 'panels') {
      quote = value * selectedService.rate;
    } else if (selectedService.calculatorType === 'windows') {
      quote = (value * selectedService.paneRate) + (value2 * selectedService.screenRate);
    }

    if (selectedService.id === 'lawn-care') {
      if (value < selectedService.minimumArea) {
        quote = selectedService.minimumValue;
      }
    } else {
      quote = Math.max(quote, selectedService.minimumValue);
    }

    setCalculatedQuote(quote);
  }, [inputValue, inputValue2, selectedService]);

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    setInputValue('');
    setInputValue2('');
    setCalculatedQuote(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Step 1: Submit form (contact details + service)
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedService) {
      toast.error('Please select a service');
      return;
    }

    if (!formData.fullName || !formData.address || !formData.suburb || !formData.postcode || !formData.email || !formData.phone) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        full_name: formData.fullName,
        address: formData.address,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        email: formData.email,
        phone: formData.phone,
        service_type: selectedService.name,
        description: formData.description || 'No additional details provided',
        use_calculator: false,
        calculated_quote: null,
        input_value: null,
        input_value_2: null,
      };

      const response = await axios.post(`${API_URL}/api/quote`, payload);
      
      if (response.data.success) {
        setSubmissionId(response.data.quote_id);
        toast.success('Details submitted! Now see your estimated minimum cost.');
        
        // If service has calculator, move to calculator step
        if (selectedService.hasCalculator) {
          setStep('calculator');
        } else {
          setStep('complete');
        }
      }
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Submit calculator result (update)
  const handleCalculatorSubmit = async () => {
    if (!submissionId || !calculatedQuote) {
      setStep('complete');
      return;
    }

    setIsSubmitting(true);

    try {
      // Submit a follow-up with calculator details for Empire's reference
      const payload = {
        full_name: formData.fullName,
        address: formData.address,
        suburb: formData.suburb,
        state: formData.state,
        postcode: formData.postcode,
        email: formData.email,
        phone: formData.phone,
        service_type: `${selectedService.name} - Calculator Follow-up`,
        description: `CALCULATOR RESULT for previous submission (ID: ${submissionId})\nEstimated minimum: $${calculatedQuote.toFixed(2)}\n\nOriginal notes: ${formData.description || 'No additional details'}`,
        use_calculator: true,
        calculated_quote: calculatedQuote,
        input_value: parseFloat(inputValue) || null,
        input_value_2: parseFloat(inputValue2) || null,
      };

      await axios.post(`${API_URL}/api/quote`, payload);
      setStep('complete');
    } catch (error) {
      console.error('Error submitting calculator result:', error);
      setStep('complete');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetAll = () => {
    setStep('form');
    setSelectedService(null);
    setInputValue('');
    setInputValue2('');
    setCalculatedQuote(null);
    setSubmissionId(null);
    setShowMaxAreaWarning(false);
    setFormData({
      fullName: '',
      address: '',
      suburb: '',
      state: 'NSW',
      postcode: '',
      email: '',
      phone: '',
      description: '',
    });
  };

  const handleClose = () => {
    resetAll();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quote-modal">
        <DialogHeader>
          <DialogTitle className="font-oswald text-2xl text-green-900 flex items-center">
            {step === 'form' && <><Info className="mr-3 text-amber-600" size={28} />Get Your Quote</>}
            {step === 'calculator' && <><Calculator className="mr-3 text-amber-600" size={28} />Your Estimated Minimum Cost</>}
            {step === 'complete' && <><CheckCircle className="mr-3 text-green-600" size={28} />Request Received</>}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {step === 'form' && 'Fill out your details to request a quote'}
            {step === 'calculator' && 'See your estimated minimum cost'}
            {step === 'complete' && 'Your quote request has been submitted'}
          </DialogDescription>
        </DialogHeader>

        {/* Progress Indicator */}
        {selectedService?.hasCalculator && step !== 'complete' && (
          <div className="flex items-center gap-2 mb-4">
            <div className={`flex-1 h-1 rounded ${step === 'form' ? 'bg-amber-500' : 'bg-green-500'}`} />
            <div className={`flex-1 h-1 rounded ${step === 'calculator' ? 'bg-amber-500' : 'bg-gray-200'}`} />
          </div>
        )}

        {/* STEP 1: FORM */}
        {step === 'form' && (
          <form onSubmit={handleFormSubmit} className="space-y-6 mt-2">
            {/* Disclaimer Banner */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start">
              <Info className="text-amber-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
              <p className="font-inter text-sm text-amber-800">
                Fill out your details and we'll show you an estimated minimum cost. All quotes are subject to revision - we use Google Earth to do our measurements.
              </p>
            </div>

            {/* Service Selection */}
            <div className="space-y-2">
              <Label className="font-inter font-medium text-gray-700">
                Select Service <span className="text-red-500">*</span>
              </Label>
              <Select 
                value={selectedService?.id || ''} 
                onValueChange={handleServiceChange}
              >
                <SelectTrigger data-testid="service-select">
                  <SelectValue placeholder="Choose a service..." />
                </SelectTrigger>
                <SelectContent>
                  {services.map(service => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedService && (
                <p className="font-inter text-xs text-gray-500 mt-1">
                  Minimum charge: <strong>{selectedService.minimumCharge}</strong>
                  {selectedService.minimumNote && <span className="block mt-1">{selectedService.minimumNote}</span>}
                </p>
              )}
            </div>

            {/* Personal Details */}
            <div className="space-y-4">
              <h3 className="font-oswald text-lg font-semibold text-gray-900 border-b pb-2">
                Your Details
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="font-inter text-sm text-gray-600">
                    Full Name <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                    className="mt-1"
                    data-testid="input-full-name"
                  />
                </div>
                <div>
                  <Label className="font-inter text-sm text-gray-600">
                    Phone <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter your phone number"
                    required
                    className="mt-1"
                    data-testid="input-phone"
                  />
                </div>
              </div>

              <div>
                <Label className="font-inter text-sm text-gray-600">
                  Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  required
                  className="mt-1"
                  data-testid="input-email"
                />
              </div>

              <div>
                <Label className="font-inter text-sm text-gray-600">
                  Street Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder="Enter your street address"
                  required
                  className="mt-1"
                  data-testid="input-address"
                />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="col-span-2 md:col-span-1">
                  <Label className="font-inter text-sm text-gray-600">
                    Suburb <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="suburb"
                    value={formData.suburb}
                    onChange={handleInputChange}
                    placeholder="Suburb"
                    required
                    className="mt-1"
                    data-testid="input-suburb"
                  />
                </div>
                <div>
                  <Label className="font-inter text-sm text-gray-600">
                    State <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.state}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, state: value }))}
                  >
                    <SelectTrigger className="mt-1" data-testid="select-state">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {australianStates.map(state => (
                        <SelectItem key={state.value} value={state.value}>
                          {state.value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="font-inter text-sm text-gray-600">
                    Postcode <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    name="postcode"
                    value={formData.postcode}
                    onChange={handleInputChange}
                    placeholder="Postcode"
                    required
                    className="mt-1"
                    data-testid="input-postcode"
                  />
                </div>
              </div>

              <div>
                <Label className="font-inter text-sm text-gray-600">
                  Brief Description / Additional Requirements
                </Label>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Any specific requirements, additional services needed (e.g., garden maintenance, house wash), or details about the job..."
                  rows={4}
                  className="mt-1"
                  data-testid="input-description"
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1 font-oswald"
                data-testid="cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !selectedService}
                className="flex-1 bg-green-900 hover:bg-green-800 text-white font-oswald btn-empire"
                data-testid="submit-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    Submit
                    {selectedService?.hasCalculator && <ArrowRight className="ml-2 h-4 w-4" />}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}

        {/* STEP 2: CALCULATOR */}
        {step === 'calculator' && selectedService?.hasCalculator && (
          <div className="space-y-6 mt-2">
            {/* Success Banner */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
              <CheckCircle className="text-green-600 mr-3 flex-shrink-0 mt-0.5" size={20} />
              <div>
                <p className="font-inter text-sm text-green-800 font-medium">
                  Your details have been received!
                </p>
                <p className="font-inter text-sm text-green-700 mt-1">
                  We'll be in contact with a customised quote. In the meantime, get an idea of the estimated minimum cost below.
                </p>
              </div>
            </div>

            {/* Calculator Section */}
            <div className="bg-slate-50 rounded-lg p-4 space-y-4">
              <h4 className="font-oswald text-lg font-semibold text-green-900 flex items-center">
                <Calculator className="mr-2 text-amber-600" size={20} />
                {selectedService.name} Calculator
              </h4>

              {/* Google Earth Info */}
              <div className="flex items-center text-sm text-blue-600 bg-blue-50 rounded-lg p-3">
                <ExternalLink size={16} className="mr-2 flex-shrink-0" />
                <a 
                  href="https://earth.google.com/web/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:underline"
                >
                  Use Google Earth's measuring tool to measure your property
                </a>
              </div>

              {/* Calculator Inputs */}
              <div className="space-y-3">
                <div>
                  <Label className="font-inter text-sm text-gray-600">
                    {selectedService.inputLabel}
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={selectedService.inputPlaceholder}
                    className="mt-1"
                    data-testid="calculator-input-1"
                    autoFocus
                  />
                </div>

                {selectedService.calculatorType === 'windows' && (
                  <div>
                    <Label className="font-inter text-sm text-gray-600">
                      {selectedService.inputLabel2}
                    </Label>
                    <Input
                      type="number"
                      min="0"
                      value={inputValue2}
                      onChange={(e) => setInputValue2(e.target.value)}
                      placeholder={selectedService.inputPlaceholder2}
                      className="mt-1"
                      data-testid="calculator-input-2"
                    />
                  </div>
                )}
              </div>

              {/* Max Area Warning */}
              {showMaxAreaWarning && (
                <div className="bg-amber-100 border border-amber-300 rounded-lg p-4 text-center" data-testid="max-area-warning">
                  <p className="font-inter text-sm text-amber-800 font-medium mb-2">
                    Your lawn size exceeds 1000m²
                  </p>
                  <p className="font-inter text-sm text-amber-700">
                    For larger properties, please contact us for a <strong>Lawn Care (Ride On)</strong> quote.
                  </p>
                </div>
              )}

              {/* Calculated Quote Display */}
              {calculatedQuote !== null && (
                <div className="bg-green-900 rounded-lg p-6 text-center" data-testid="calculated-quote">
                  <p className="font-inter text-sm text-white/80 mb-1">
                    Estimated Minimum Cost
                  </p>
                  <p className="font-oswald text-4xl font-bold text-white">
                    ${calculatedQuote.toFixed(2)}
                    {selectedService.gstInclusive ? (
                      <span className="text-lg font-normal text-white/80 ml-2">inc. GST</span>
                    ) : (
                      <span className="text-lg font-normal text-white/80 ml-2">+ GST</span>
                    )}
                  </p>
                  <p className="font-inter text-xs text-amber-300 mt-3">
                    This is a minimum estimate. We'll confirm the final quote after property assessment.
                  </p>
                </div>
              )}

              {/* Pricing Info */}
              <div className="text-sm text-gray-600 bg-white rounded-lg p-3 border border-gray-200">
                <p><strong>Minimum charge:</strong> {selectedService.minimumCharge}</p>
                {selectedService.minimumNote && (
                  <p className="mt-1 text-xs text-gray-500">{selectedService.minimumNote}</p>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4">
              <Button
                type="button"
                onClick={handleCalculatorSubmit}
                disabled={isSubmitting}
                className="flex-1 bg-green-900 hover:bg-green-800 text-white font-oswald btn-empire"
                data-testid="close-calculator-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  calculatedQuote ? 'Send Result & Finish' : 'Finish'
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: COMPLETE */}
        {step === 'complete' && (
          <div className="text-center py-8">
            <div className="mx-auto w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="text-green-600" size={40} />
            </div>
            <h3 className="font-oswald text-2xl font-bold text-green-900 mb-3">
              Thank You!
            </h3>
            <p className="font-inter text-gray-600 mb-2">
              Your quote request has been received.
            </p>
            <p className="font-inter text-sm text-gray-500 mb-6">
              We'll be in contact with you shortly to arrange a property assessment and provide a customised quote.
            </p>
            <Button
              onClick={handleClose}
              className="bg-green-900 hover:bg-green-800 text-white font-oswald px-8"
              data-testid="complete-close-btn"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
